import Div from "@jumbo/shared/Div/Div";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import MuiTableWrapper from "app/pages/Docgen/components/common/mui-table-wrapper/MuiTableWrapper";
import {useFormik} from "formik";
import {useEffect, useState, useRef} from "react";
import {toast} from "react-toastify";
import * as Yup from "yup";
import {get, put} from "../../../common/network";
import {categories, categoriesSubCategoriesMap} from "../../../constants/category_mappings";
import {DateFormatter} from "../../../constants/date_format";
import {DateFormatsOptions, VALIDATION_MESSAGE} from "../../../constants/index";
import UploadForm from "./UploadForm";
import { fetchCountryData } from "../../../api/countries";
import dayjs from "dayjs";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Typography from "@mui/material/Typography";
import SubjectLinesModal from "../../../components/common/modal-preview/SubjectLineModal";

const ModifyDetails = ({formData, id, loadingFormData}) => {
    const [uploadedForm, setUploadedForm] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDownloadLoading, setIsDownloadLoading] = useState(null);
    const [rowsSelection, setRowsSelection] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [countriesMapping, setCountriesMapping] = useState([]);
    const inputRef = useRef(null);
    const [usedChips, setUsedChips] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const today = dayjs();

    const presetOptions = [
        "Application Number",
        "Attorney Docket Number",
        "Filing Date",
        "Publication Date",
        "Status"
    ];

    const additionalOptions = [
        "Title",
        "Local Filing Date",
        "Matter Sub-Matter Type",
        "Matter Type",
        "Correspondence Customer Number",
        "Matter Entity Size",
        "Priority Date",
        "Priority Application Number",
        "Pct Application Number",
        "Pct Date",
        "Patent Number",
        "Grant Date",
        "Country Code",
        "Country Name",
        "Publication Number",
        "Client Reference Number",
        "Agent Reference Number",
        "Billing Reference Number",
        ...(localStorage.getItem("TENANT_NAME") === "knobbe" ? [
            "Trademark",
            "Mark Type",
            "Intent to Use",
            "Last Renewal Date",
            "First Use",
            "First Use in Commerce",
            "Allowance"
        ] : [])
    ];



    const sortedOptions = [...additionalOptions].sort((a, b) =>
        a.localeCompare(b)
    );

    const tokenize = (value) => {
        const matches = value.match(/\[([^\]]+)\]/g);
        return matches ? matches.map(v => v.replace(/[\[\]]/g, "").trim()) : [];
    };

    useEffect(() => {
        fetchCountryMapping();
    }, []);

    // Validation schema defined
    const validationSchema = Yup.object({
        form_code: Yup.string().required(VALIDATION_MESSAGE.required),
        form_name: Yup.string().required(VALIDATION_MESSAGE.required),
        filingName: Yup.string().nullable(),
        defaultCategory: Yup.string().nullable(),
        defaultSubCategory: Yup.string().nullable(),
        country: Yup.string().nullable(),
        dateFormat: Yup.string().nullable(),
        subject_lines: Yup.string().nullable()
    });

    // Added formik for form and fields handling
    const formik = useFormik({
        initialValues: {...formData?.data},
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: async values => {
            try {
                setIsLoading(true);
                const formData = new FormData();
                formData.append("form_code", values.form_code);
                formData.append("form_name", values.form_name);
                if (values.filingName)
                    formData.append("filing_name", values.filingName);
                if (values.defaultCategory)
                    formData.append("default_category", values.defaultCategory);
                if (values.defaultSubCategory)
                    formData.append("default_sub_category", values.defaultSubCategory);
                if (values.country)
                    formData.append("country", values.country);
                if (values.dateFormat)
                    formData.append("dateFormat", values.dateFormat);
                if (values.subject_lines)
                    formData.append("subject_lines", values.subject_lines || "");
                if (uploadedForm) {
                    const file = uploadedForm.file; 
                    formData.append("file", file, file.name);
                }
                const response = await put(`docgen/forms/${id}`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                });

                if (response?.success === true) {
                    toast.success(response.message);
                }
            } catch (error) {
            } finally {
                setIsLoading(false);
            }
        }
    });
    const lastValidValue = useRef(formik.values.subject_lines || "");

    /**
     * Upload handler function for uploading matters
     * @param {Array} files Files object
     */
    const uploadHandler = (files) => {
        if (files) {
            let file = files[0];
            let fileName = file.name;
            let fileSize = (file.size / 1024).toFixed(2);
            setUploadedForm({
                file: file, // Store the file object for later use
                label: `${fileName} - ${fileSize}kb`
            });
        }
    };
    //#endregion
    const fetchCountryMapping = async () => {
        const data = await fetchCountryData();
        if (data) {
            const countries = data
                .map(country => ({
                    label: country.name,
                    value: country.code,
                }))
                .sort((a, b) => a.label.localeCompare(b.label));

            setCountriesMapping(countries);
        }
    };


    //#region for fake data
    const columns = [
        {
            field: "updated_by",
            header: "Updated By",
            render: (value, row) => {
                const first_name = row?.user?.first_name;
                const last_name = row?.user?.last_name;
                return `${first_name} ${last_name}`;
            }
        },
        {
            field: "updated_at",
            header: "Update Date",
            render: (value, row) => {
                const date = new Date(row?.updated_at);
                return DateFormatter(date);
            }
        },
        {
            field: "download_form",
            header: "Download Form",
            render: (value, row) => {
                const handleDownload = async () => {
                    try {
                        setIsDownloadLoading(row.id);
                        const response = await get(`docgen/downloadhistoryform/${row?.id}`, {
                            responseType: "blob" // Specify that you expect a Blob response
                        });

                        // Create a Blob from the response content
                        const blob = new Blob([response], {type: response.type});

                        // Create a URL for the blob response
                        const url = URL.createObjectURL(blob);

                        // Create a hidden anchor element
                        const anchor = document.createElement("a");
                        anchor.style.display = "none";
                        anchor.href = url;
                        anchor.download = row.new_value.pdf_path.split("/").pop(); // Set the desired file name

                        // Append the anchor to the document body
                        document.body.appendChild(anchor);

                        // Simulate a click on the anchor to trigger the download
                        anchor.click();

                        // Remove the anchor from the document body
                        document.body.removeChild(anchor);
                    } catch (error) {
                    } finally {
                        setIsDownloadLoading(null);
                    }
                };
                return (
                    <div>
                        {isDownloadLoading == row.id ? (
                            <Div
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    ".btn-loader-icon": {
                                        width: "20px",
                                        height: "20px",
                                        border: "3px solid gray",
                                        borderColor: "transparent gray gray gray",
                                        borderRadius: "50%",
                                        animation: "rotate .5s linear infinite"
                                    }
                                }}
                            >
                                <div className="btn-loader-icon"/>
                            </Div>
                        ) : (
                            <IconButton variant="text" className="listing-btn" sx={{textDecoration: "none !important"}} onClick={handleDownload}>
                                <FileDownloadOutlinedIcon
                                    sx={{
                                        fontSize: "20px",
                                        color: "inherit"
                                    }}
                                />
                            </IconButton>
                        )}
                    </div>
                );
            }
        }
    ];

    const [data, setData] = useState([]);
    const [isFetching, setIsFetching] = useState(false);

    const fetchData = async () => {
        setIsFetching(true);
        try {
            const response = await get(`docgen/formhistory/${id}`);
            const rows = response?.data || [];
            const rowsWithChanges = rows.map((row) => ({
                ...row,
                old_value: JSON.parse(row.old_value),
                new_value: JSON.parse(row.new_value)
            }));

            setData(rowsWithChanges);
        } catch (error) {
        } finally {
            setIsFetching(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleChipClick = (option) => {
        if (usedChips.includes(option)) return;

        const token = `[${option}]`;
        const input = inputRef.current;
        let value = formik.values.subject_lines || "";

        const start = input?.selectionStart ?? value.length;
        const before = value.slice(0, start);
        const after = value.slice(start);

        const needsSpaceBefore =
            before.length > 0 && !before.endsWith(" ");
        const needsSpaceAfter =
            after.length > 0 && !after.startsWith(" ");

        const updated =
            `${before}${needsSpaceBefore ? " " : ""}` +
            `${token}` +
            `${needsSpaceAfter ? " " : ""}${after}`;

        formik.setFieldValue("subject_lines", updated);
        setUsedChips(prev => [...prev, option]);
        lastValidValue.current = updated;
    };

    const handleChange = (e) => {
        formik.setFieldValue("subject_lines", e.target.value);
        lastValidValue.current = e.target.value;
    };

    useEffect(() => {
        const tokens = tokenize(formik.values.subject_lines || "");
        setUsedChips(tokens);
        lastValidValue.current = formik.values.subject_lines || "";
    }, [formik.values.subject_lines]);

    const handleKeyDown = (e) => {
        const input = inputRef.current;
        if (!input) return;

        const value = formik.values.subject_lines || "";
        const cursorPos = input.selectionStart;

        const tokenRegex = /\[[^\[\]]+\]/g;
        let match;

        const navigationKeys = [
            "ArrowLeft",
            "ArrowRight",
            "ArrowUp",
            "ArrowDown",
            "Home",
            "End",
            "Tab"
        ];

        //make keys non-functional if inside the placeholder
        while ((match = tokenRegex.exec(value)) !== null) {
            const start = match.index;
            const end = start + match[0].length;

            // strictly inside (not at boundaries)
            if (cursorPos > start && cursorPos < end) {

                //allow navigation 
                if (navigationKeys.includes(e.key)) {
                    return;
                }
                e.preventDefault();
                return;
            }
        }
        
        // Handle Backspace key only if just after placeholder
        if (e.key === "Backspace") {
            const leftPart = value.slice(0, cursorPos);
            const rightPart = value.slice(cursorPos);

            const tokenMatch = leftPart.match(/\[[^\[\]]+\]$/);

            if (tokenMatch) {
                const tokenText = tokenMatch[0].replace(/[\[\]]/g, "").trim();

                if (usedChips.includes(tokenText)) {
                    const newValue =
                        value.slice(0, leftPart.length - tokenMatch[0].length) +
                        rightPart;

                    formik.setFieldValue("subject_lines", newValue);

                    setUsedChips(prev =>
                        prev.filter(t => t !== tokenText)
                    );

                    const newCursor =
                        leftPart.length - tokenMatch[0].length;

                    setTimeout(() => {
                        input.setSelectionRange(newCursor, newCursor);
                    }, 0);

                    e.preventDefault();
                    return;
                }
            }
        }
        // Handle Delete (only if just before placeholder)
        if (e.key === "Delete") {
            const leftPart = value.slice(0, cursorPos);
            const rightPart = value.slice(cursorPos);

            const tokenMatch = rightPart.match(/^\[[^\[\]]+\]/);

            if (tokenMatch) {
                const tokenText = tokenMatch[0].replace(/[\[\]]/g, "").trim();

                if (usedChips.includes(tokenText)) {
                    const newValue =
                        leftPart + rightPart.slice(tokenMatch[0].length);

                    formik.setFieldValue("subject_lines", newValue);

                    setUsedChips(prev =>
                        prev.filter(t => t !== tokenText)
                    );

                    setTimeout(() => {
                        input.setSelectionRange(cursorPos, cursorPos);
                    }, 0);

                    e.preventDefault();
                    return;
                }
            }
        }
    };

    const handleCheckboxChange = (option, checked) => {
        const token = `[${option}]`;
        const input = inputRef.current;
        let value = formik.values.subject_lines || "";

        if (checked) {
            if (usedChips.includes(option)) return;

            const start = input?.selectionStart ?? value.length;
            const before = value.slice(0, start);
            const after = value.slice(start);

            // Ensure single space separation
            const needsSpaceBefore =
                before.length > 0 && !before.endsWith(" ");
            const needsSpaceAfter =
                after.length > 0 && !after.startsWith(" ");

            const updated =
                `${before}${needsSpaceBefore ? " " : ""}` +
                `${token}` +
                `${needsSpaceAfter ? " " : ""}${after}`;

            formik.setFieldValue("subject_lines", updated);

            setUsedChips(prev => [...prev, option]);
        } else {
            const updated = value
                .replace(token, "")
                .replace(/\s{2,}/g, " ")
                .trim();

            formik.setFieldValue("subject_lines", updated);

            setUsedChips(prev => prev.filter(t => t !== option));
        }
    };

    return (
        <div className="page-content-wrapper position-relative">
            {(isLoading || loadingFormData) ? (
                <Div
                    sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        position: "absolute",
                        ".btn-loader-icon": {
                            width: "50px",
                            height: "50px",
                            border: "3px solid gray",
                            borderColor: "transparent gray gray gray",
                            borderRadius: "50%",
                            animation: "rotate .5s linear infinite"
                        }
                    }}
                >
                    <div className="btn-loader-icon"/>
                </Div>
            ) : (
                <Box className="w-100">
                    <Div sx={{pt: "17px"}}>
                        <form onSubmit={formik.handleSubmit}>
                            <div className="container mb-4">
                                <div className="row">
                                    <div className="col-12">
                                        <div className="form-group">
                                            <label htmlFor="upload_file" className="upload_file_label">Upload Form Version</label>
                                            <UploadForm onUpload={uploadHandler}/>
                                            {uploadedForm && (
                                                <div className="mt-3">
                                                    <Chip icon={<AttachFileOutlinedIcon sx={{fontSize: "18px"}}/>} className="uploaded-form-details" label={uploadedForm.label} onDelete={() => setUploadedForm(null)}/>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="w-100"/>
                                    <div className="col-lg-4">
                                        <div className="form-group">
                                            <InputLabel htmlFor="form_code">Form Code</InputLabel>
                                            <TextField fullWidth size="small" id="form_code" name="form_code" value={formik.values.form_code} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.form_code && Boolean(formik.errors.form_code)} helperText={formik.touched.form_code && formik.errors.form_code}/>
                                        </div>
                                    </div>
                                    <div className="col-lg-4">
                                        <div className="form-group">
                                            <InputLabel htmlFor="form_name">Form Name</InputLabel>
                                            <TextField fullWidth size="small" id="form_name" name="form_name" value={formik.values.form_name} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.form_name && Boolean(formik.errors.form_name)} helperText={formik.touched.form_name && formik.errors.form_name}/>
                                        </div>
                                    </div>
                                    <div className="col-lg-4">
                                        <div className="form-group">
                                            <InputLabel htmlFor="filingName">Filing Name</InputLabel>
                                            <TextField fullWidth size="small" id="filingName" name="filingName" value={formik.values.filingName} onChange={formik.handleChange} onBlur={formik.handleBlur}/>
                                        </div>
                                    </div>
                                    <div className="col-lg-4">
                                        <div className="form-group">
                                            <InputLabel htmlFor="defaultCategory">Category</InputLabel>
                                            <FormControl size="small" sx={{mr: 1, width: "100%"}}>
                                                <Select id="defaultCategory" name="defaultCategory" value={formik.values.defaultCategory} onChange={formik.handleChange} displayEmpty renderValue={formik.values.defaultCategory ? undefined : () => "Select Category"} onBlur={formik.handleBlur}>
                                                    {categories?.map((category) => (<MenuItem key={category} value={category}>{category}</MenuItem>))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                    </div>
                                    <div className="col-lg-4">
                                        <div className="form-group">
                                            <InputLabel htmlFor="defaultSubCategory">Sub-Category</InputLabel>
                                            <FormControl size="small" sx={{mr: 1, width: "100%"}}>
                                                <Select id="defaultSubCategory" name="defaultSubCategory" value={formik.values.defaultSubCategory} onChange={formik.handleChange} displayEmpty renderValue={selected => (!selected ? "Select Sub-category" : selected)} onBlur={formik.handleBlur}>
                                                    {categoriesSubCategoriesMap[formik.values.defaultCategory]?.map(sub_category => (<MenuItem key={sub_category} value={sub_category}>{sub_category}</MenuItem>))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                    </div>
                                    <div className="col-lg-4">
                                        <div className="form-group">
                                            <InputLabel htmlFor="country">Country</InputLabel>
                                            <FormControl size="small" sx={{mr: 1, width: "100%"}}>
                                                <Select id="country" name="country" value={formik.values.country} onChange={formik.handleChange} displayEmpty renderValue={selected => (!selected ? "Select Country" : selected)} onBlur={formik.handleBlur}>
                                                    {countriesMapping?.map(country => (<MenuItem key={country.value} value={country.value}>{country.label}</MenuItem>))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                    </div>
                                    <div className="col-lg-4">
                                        <div className="form-group">
                                            <InputLabel htmlFor="dateFormat">
                                                Date Format
                                                <Tooltip title="This field defines the date format for this custom form. For example, if you select [[YYYY-MM-DD]], all dates in this form will be displayed in that format.">
                                                    <IconButton size="small" sx={{ ml: 0.5 }}>
                                                        <InfoOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </InputLabel>
                                            <FormControl size="small" sx={{mr: 1, width: "100%"}}>
                                                <Select id="dateFormat" name="dateFormat" value={formik.values.dateFormat} onChange={formik.handleChange} displayEmpty renderValue={selected => (!selected ? "Select Date Format" : selected)} onBlur={formik.handleBlur}>
                                                    {DateFormatsOptions?.map(format => (
                                                        <MenuItem key={format} value={format}>
                                                            {format} ({today.format(format)})
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                    </div>
                                    <div className="col-lg-8">
                                        <div className="form-group">
                                            <InputLabel>
                                                Subject Line
                                                <Tooltip title="This field defines the matter values to be added in the subject of the generated EML file">
                                                    <IconButton size="small" sx={{ ml: 0.5 }}>
                                                        <InfoOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </InputLabel>

                                            <TextField
                                                fullWidth
                                                size="small"
                                                name="subject_lines"
                                                value={formik.values.subject_lines || ""}
                                                onChange={handleChange}
                                                onKeyDown={handleKeyDown}
                                                placeholder="Enter or select subject lines"
                                                inputRef={inputRef}
                                                sx={{ mb: 1 }}
                                            />

                                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                                {presetOptions.map((option) => (
                                                    <Chip
                                                        key={option}
                                                        label={option}
                                                        clickable
                                                        disabled={usedChips.includes(option)}
                                                        onClick={() => handleChipClick(option)}
                                                    />
                                                ))}
                                            </Stack>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    mt: 1,
                                                    color: "#1976d2",
                                                    cursor: "pointer",
                                                    textDecoration: "underline",
                                                    width: "fit-content"
                                                }}
                                                onClick={() => setOpenModal(true)}
                                            >
                                                Add more fields
                                            </Typography>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <Button
                                            type="submit"
                                            sx={{
                                                width: "100%",
                                                maxWidth: "200px",
                                                mt: "20px",
                                                mb: "10px"
                                            }}
                                            className="btn-theme d-block ms-auto"
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </Div>
                    <Div>
                        <div className="container">
                            <div className="row">
                                <div className="col-12">
                                    <MuiTableWrapper columns={columns} data={data} isFetching={isFetching} searchValue="" filterColumn="all" selectedRows={selectedRows} setSelectedRows={setSelectedRows} hasCheckboxesColumn={false} rowsSelection={rowsSelection} setRowsSelection={setRowsSelection}/>
                                </div>
                            </div>
                        </div>
                    </Div>
                </Box>
            )}
            <SubjectLinesModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                options={sortedOptions}
                usedChips={usedChips}
                onCheckboxChange={handleCheckboxChange}
            />
        </div>
    );
};


export default ModifyDetails;